import {TreeItem, WsNode} from './treeTypes';

/**
 * Fast, fetch-free tree for the "All workstreams" view: group the flat company
 * list by pillar (a string category) into `Pillar → workstreams`. This avoids the
 * per-node parent walk, which is unworkable at company scale. Deeper narrative/sub
 * nesting isn't shown here (there is no bulk edge source) — open a workstream for
 * its sub-workstreams.
 */
// Workstreams with no pillar are grouped here (cross-cutting / enablement work).
export const ENABLEMENT_GROUP_LABEL = 'Other workstreams';
export const ENABLEMENT_GROUP_DESCRIPTION =
  'Other workstreams support product work but do not connect directly with the company strategy.';

/**
 * Order pillar groups to match the `/workstreams` sidebar: follow the canonical
 * narrative pillar order (`pillarOrder`, from `usePillars`), then any pillars not
 * in that list alphabetically, and always sort the catch-all Enablement group
 * last.
 */
export const buildPillarGroupedRoots = (
  nodes: WsNode[],
  pillarOrder: readonly string[] = []
): TreeItem[] => {
  const byPillar = new Map<string, WsNode[]>();
  for (const node of nodes) {
    const key = node.pillar?.trim() || ENABLEMENT_GROUP_LABEL;
    if (!byPillar.has(key)) byPillar.set(key, []);
    byPillar.get(key)!.push(node);
  }

  // A workstream sits in the one pillar the walk reached it from first, but it
  // still nests under its parents in other pillars (Tech › SF3).
  const allById = new Map(nodes.map((node) => [node.id, node]));
  const orderIndex = new Map(pillarOrder.map((name, index) => [name, index]));
  const rankOf = (pillar: string): number => {
    if (pillar === ENABLEMENT_GROUP_LABEL) return Number.MAX_SAFE_INTEGER;
    const index = orderIndex.get(pillar);
    // Pillars missing from the canonical list sort after the known ones.
    return index !== undefined ? index : pillarOrder.length;
  };

  return [...byPillar.entries()]
    .sort(([a], [b]) => {
      const rankDelta = rankOf(a) - rankOf(b);
      if (rankDelta !== 0) return rankDelta;
      return a.localeCompare(b);
    })
    .map(([pillar, group], index) => ({
      node: {
        id: -(index + 1), // synthetic, never collides with real (positive) ids
        name: pillar,
        pillar,
        isPillarGroup: true,
      },
      level: 1,
      // Nest the pillar's workstreams by their parent edges (L1 → L2 → …), so the
      // rail mirrors the /workstreams sub-workstream hierarchy.
      children: buildNestedSubtree(group, allById, 2),
    }));
};

/**
 * Build a nested `TreeItem[]` from a flat set of workstreams. `startLevel` is
 * the depth of the top-most rows (2 = directly under a pillar group).
 *
 * The order is the server's: only a null `parentId` (a direct child of the
 * pillar) makes a root, in the order the nodes arrive (the narrative walk, saved
 * order); each node's children are its `childIds` in `byId` (every pillar's
 * nodes, not just this set), in their saved order, so a workstream under several
 * parents sits at its own place in each, even a parent in another pillar. The
 * same roots the narrative-only list shows for the pillar; deeper nodes whose
 * parents aren't in the set are left out rather than floated up.
 */
const buildNestedSubtree = (
  nodes: WsNode[],
  byId: ReadonlyMap<number, WsNode>,
  startLevel: number
): TreeItem[] => {
  const byName = (a: WsNode, b: WsNode) => a.name.localeCompare(b.name);
  // A nav tree from before `child_ids` (the API deploys separately) still
  // nests: children from the `parentIds` edges, by name, as it used to.
  const hasChildIds = nodes.some((node) => (node.childIds?.length ?? 0) > 0);
  const legacyChildIds = new Map<number, number[]>();
  if (!hasChildIds) {
    for (const node of nodes.slice().sort(byName)) {
      for (const parent of node.parentIds ?? []) {
        if (parent === node.id || !byId.has(parent)) continue;
        legacyChildIds.set(parent, [...(legacyChildIds.get(parent) ?? []), node.id]);
      }
    }
  }
  const childIdsOf = (node: WsNode) =>
    hasChildIds ? (node.childIds ?? []) : (legacyChildIds.get(node.id) ?? []);

  // `path` guards against cycles in the edges.
  const buildFrom = (ids: number[], level: number, path: Set<number>): TreeItem[] =>
    ids.flatMap((id) => {
      const node = byId.get(id);
      if (!node || path.has(id)) return [];
      const nextPath = new Set(path).add(id);
      return [{node, level, children: buildFrom(childIdsOf(node), level + 1, nextPath)}];
    });
  const roots = nodes.filter((node) => node.parentId == null);
  return buildFrom(
    (hasChildIds ? roots : roots.slice().sort(byName)).map((node) => node.id),
    startLevel,
    new Set()
  );
};

// A pillar of one workstream is that workstream: it leads the pillar
// (Multi-Product › Multi-Product › MP1…, Bridge › Nick's workstream), rather
// than the pillar holding workstreams side by side (Applications › A1, A2…).
const leadOf = (root: TreeItem): TreeItem | undefined => {
  const only = root.children.length === 1 ? root.children[0] : undefined;
  return root.node.isPillarGroup && root.node.pillar && only && !only.node.isPillarGroup
    ? only
    : undefined;
};

/** Each led pillar's lead workstream id → its pillar. */
export const getPillarLeads = (roots: TreeItem[]): Map<number, string> =>
  new Map(
    roots.flatMap((root) => {
      const lead = leadOf(root);
      return lead ? [[lead.node.id, root.node.pillar!] as const] : [];
    })
  );

const shiftLevels = (items: TreeItem[], by: number): TreeItem[] =>
  items.map((item) => ({
    ...item,
    level: item.level + by,
    children: shiftLevels(item.children, by),
  }));

/**
 * The rail: a led pillar's row stands for its lead workstream (its
 * sub-workstreams right under it), dropping the repeated level.
 */
export const mergePillarLeads = (roots: TreeItem[]): TreeItem[] =>
  roots.map((root) => {
    const lead = leadOf(root);
    if (!lead) return root;
    return {
      ...root,
      node: {
        ...root.node,
        // Shown only if the pillar has no narrative of its own.
        narrativeUrl: lead.node.narrativeUrl,
        roadmapUrl: lead.node.roadmapUrl,
      },
      children: shiftLevels(lead.children, -1),
    };
  });

/**
 * `rows` in the rail's order: pillars in theirs, each pillar's workstreams
 * top to bottom (a workstream under several parents takes its first place).
 * Rows the rail doesn't show keep their order, after the rest.
 */
export const sortByRailOrder = <T extends {id: number}>(rows: T[], roots: TreeItem[]): T[] => {
  const position = new Map<number, number>();
  const visit = (items: TreeItem[]) => {
    for (const item of items) {
      if (!item.node.isPillarGroup && !position.has(item.node.id)) {
        position.set(item.node.id, position.size);
      }
      visit(item.children);
    }
  };
  visit(roots);
  const rank = (row: T) => position.get(row.id) ?? Number.MAX_SAFE_INTEGER;
  return [...rows].sort((a, b) => rank(a) - rank(b));
};
